import './perks-post-filter-modal.component.scss';

import { h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';

import { SpinnerComponent } from '../../../../common/components/spinner/spinner.component';
import { IconImage } from '../../../../common/icons/icon-image';
import { IconMerch } from '../../../../common/icons/icon-merch';
import { IconMusic } from '../../../../common/icons/icon-music';
import { IconText } from '../../../../common/icons/icon-text';
import { IconVideoTab } from '../../../../common/icons/icon-video-tab';
import { Pageable } from '../../../../common/page/pageable.model';
import { useFeatureFlag } from '../../../../feature-flags/hooks/use-feature-flag.hook';
import { FeatureFlagName } from '../../../../feature-flags/models/feature-flag-names.enum';
import { useTranslation } from '../../../../i18n';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { buttonize } from '../../../../utils/a11y.utils';
import type { Author, Tag } from '../../../model/post.interface';
import type { PostFilter } from '../../../model/post-filter.model';
import {
  selectAuthors,
  selectAuthorsLoading,
  selectTags,
  selectTagsLoading,
} from '../../../store/perks.selectors';
import { loadAuthors, loadTags } from '../../../store/perks.thunk';

interface Props {
  postFilterProp: PostFilter;
  cssVars?: Record<string, string>;
  onClose: (cancel: PostFilter | null) => void;
}

const getInitials = (name: string) => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

export const PerksPostFilterModal = ({ postFilterProp, cssVars, onClose }: Props) => {
  const { t } = useTranslation();
  const isAudioPostsEnabled = useFeatureFlag(FeatureFlagName.AUDIO_POSTS);
  const dispatch = useAppDispatch();
  const loading = useAppSelector(selectTagsLoading) || useAppSelector(selectAuthorsLoading);
  const tags = useAppSelector(selectTags);
  const authors = useAppSelector(selectAuthors);
  const [postFilter, setPostFilter] = useState<PostFilter>(postFilterProp);

  useEffect(() => {
    dispatch(loadTags(Pageable.of(0, 100)));
    dispatch(loadAuthors(Pageable.of(0, 100)));
  }, [dispatch]);

  const handleClose = (cancel: boolean) => {
    if (cancel) {
      onClose(null);
    } else {
      onClose(postFilter);
    }
  };

  // Clear all is an action, not a draft edit: apply the empty filter and close in one tap
  const handleClearAll = () => {
    onClose({ types: [], tagIds: [], authors: [] });
  };

  const isActiveType = (type: string) => {
    return (postFilter?.types || []).indexOf(type) > -1;
  };

  const handlePostType = (type: string) => {
    const types = postFilter?.types || [];
    const index = types.indexOf(type);

    index === -1 ? types.push(type) : types.splice(index, 1);

    setPostFilter((prev) => ({ ...prev, types }));
  };

  const isTagSelected = (tag: Tag) => {
    return (postFilter?.tagIds || []).indexOf(tag.id) > -1;
  };

  const toggleTag = (tag: Tag) => {
    setPostFilter((prev) => ({
      ...prev,
      tagIds: (prev?.tagIds || []).includes(tag.id)
        ? (prev?.tagIds || []).filter((id) => id !== tag.id)
        : [...(prev?.tagIds || []), tag.id],
    }));
  };

  const isAuthorSelected = (author: Author) => {
    return (postFilter?.authors || []).indexOf(author.name) > -1;
  };

  const toggleAuthor = (author: Author) => {
    setPostFilter((prev) => ({
      ...prev,
      authors: (prev?.authors || []).includes(author.name)
        ? (prev?.authors || []).filter((name) => name !== author.name)
        : [...(prev?.authors || []), author.name],
    }));
  };

  const activeFilterCount =
    (postFilter?.types?.length || 0) +
    (postFilter?.tagIds?.length || 0) +
    (postFilter?.authors?.length || 0);

  const modalRef = useRef<HTMLDivElement>(null);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);

  const handleDragStart = (e: TouchEvent) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
  };

  const handleDragMove = (e: TouchEvent) => {
    const dy = e.touches[0].clientY - touchStartY.current;
    const container = modalRef.current?.parentElement as HTMLElement | null;
    if (dy > 0 && container) {
      container.style.transition = 'none';
      container.style.transform = `translateY(${dy}px)`;
    }
  };

  const handleDragEnd = (e: TouchEvent) => {
    const dy = e.changedTouches[0].clientY - touchStartY.current;
    const velocity = dy / (Date.now() - touchStartTime.current); // px/ms
    const container = modalRef.current?.parentElement as HTMLElement | null;
    const shouldClose = dy > 80 || (dy > 20 && velocity > 0.4);

    if (shouldClose) {
      if (container) {
        container.style.transition = 'transform 0.28s ease-out';
        container.style.transform = 'translateY(100%)';
        container.addEventListener('transitionend', () => handleClose(true), { once: true });
      } else {
        handleClose(true);
      }
    } else if (container) {
      container.style.transition = 'transform 0.25s ease-out';
      container.style.transform = '';
      container.addEventListener(
        'transitionend',
        () => {
          container.style.transition = '';
        },
        { once: true }
      );
    }
  };

  return (
    <div class="single-post-filter-modal" style={cssVars} ref={modalRef}>
      <div
        class="single-modal-drag-zone"
        onTouchStart={handleDragStart}
        onTouchMove={handleDragMove}
        onTouchEnd={handleDragEnd}
      >
        <div class="single-modal-drag-handle-area">
          <div class="single-modal-drag-handle" />
        </div>
        <div class="single-modal-header">
          <h3 class="single-modal-title">{t('perks.filterPosts')}</h3>
          {activeFilterCount > 0 && (
            <button
              class="single-modal-clear-all"
              onClick={handleClearAll}
              onTouchStart={(e) => e.stopPropagation()}
            >
              {t('perks.clearAll')}
            </button>
          )}
        </div>
      </div>

      <div class="single-modal-body">
        {loading && (
          <section class="single-post-filter-modal-loading">
            <SpinnerComponent />
          </section>
        )}

        {!loading && (
          <>
            <div class="single-filter-group">
              <span class="single-filter-label">{t('perks.postType')}</span>
              <div class="single-filter-group-options">
                <button
                  class={`btn button ${!isActiveType('TEXT') ? 'btn-outline' : ''}`}
                  onClick={() => handlePostType('TEXT')}
                >
                  <IconText />
                  <span>{t('perks.postTypeText')}</span>
                </button>
                <button
                  class={`btn button ${!isActiveType('IMAGE') ? 'btn-outline' : ''}`}
                  onClick={() => handlePostType('IMAGE')}
                >
                  <IconImage />
                  <span>{t('perks.postTypeImages')}</span>
                </button>
                <button
                  class={`btn button ${!isActiveType('VIDEO') ? 'btn-outline' : ''}`}
                  onClick={() => handlePostType('VIDEO')}
                >
                  <IconVideoTab />
                  <span>{t('perks.postTypeVideos')}</span>
                </button>
                {isAudioPostsEnabled && (
                  <button
                    class={`btn button ${!isActiveType('AUDIO') ? 'btn-outline' : ''}`}
                    onClick={() => handlePostType('AUDIO')}
                  >
                    <IconMusic />
                    <span>{t('perks.postTypeAudio')}</span>
                  </button>
                )}
                <button
                  class={`btn button ${!isActiveType('PRODUCT') ? 'btn-outline' : ''}`}
                  onClick={() => handlePostType('PRODUCT')}
                >
                  <IconMerch />
                  <span>{t('perks.postTypeProducts')}</span>
                </button>
              </div>
            </div>

            {tags && tags.content.length > 0 && (
              <div class="single-filter-group">
                <span class="single-filter-label">{t('perks.tags')}</span>
                <div class="single-filter-group-options single-tags">
                  {tags.content.map((tag) => (
                    <span
                      key={tag.id}
                      class={`single-tag ${isTagSelected(tag) ? 'selected' : ''}`}
                      {...buttonize(() => toggleTag(tag))}
                      aria-pressed={isTagSelected(tag)}
                    >
                      {tag.name}
                      {isTagSelected(tag) && <span class="single-tag-remove">×</span>}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {authors && authors.content.length > 0 && (
              <div class="single-filter-group">
                <span class="single-filter-label">{t('perks.authors')}</span>
                <div class="single-filter-group-options single-authors">
                  {authors.content.map((author) => (
                    <label
                      key={author.name}
                      class={`single-author${isAuthorSelected(author) ? ' selected' : ''}`}
                    >
                      <div class="single-author-avatar">
                        {author.imageUrl ? (
                          <img src={author.imageUrl} alt={author.name} />
                        ) : (
                          <span class="single-author-initials">{getInitials(author.name)}</span>
                        )}
                      </div>
                      <span class="author-name">{author.name}</span>
                      <input
                        type="checkbox"
                        checked={isAuthorSelected(author)}
                        onChange={() => toggleAuthor(author)}
                      />
                      <span class="checkmark"></span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div class="single-modal-footer single-action-list inline--btns">
        <button class="btn btn-outline button" onClick={() => handleClose(true)}>
          {t('common.cancel')}
        </button>
        <button
          class={`btn button${activeFilterCount > 0 ? '' : ' btn-outline'}`}
          onClick={() => handleClose(false)}
        >
          {t('perks.applyFilters')}
          {activeFilterCount > 0 && <span class="single-filter-count">{activeFilterCount}</span>}
        </button>
      </div>
    </div>
  );
};
